import { useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Text,
  Avatar,
  Badge,
  Spinner,
  Heading,
  Link,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLatestSolves } from '../hooks/useLatestSolves';
import { formatTime } from '../utils/formatTime';

export const LatestSolves = () => {
  const { t } = useTranslation();
  const { getLatestSolves, loading, error } = useLatestSolves();
  const [solves, setSolves] = useState<any[]>([]);
  const accentColor = useColorModeValue('orange.500', 'orange.300');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  useEffect(() => {
    const fetchSolves = async () => {
      const latestSolves = await getLatestSolves(5);
      setSolves(latestSolves);
    };

    fetchSolves();
    // Refresh every minute
    const interval = setInterval(fetchSolves, 60000);
    return () => clearInterval(interval);
  }, [getLatestSolves]);

  if (loading && solves.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Spinner color={accentColor} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Text color="red.500">{t('common.error')}</Text>
      </Box>
    );
  }

  const getTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return 'a while ago';
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="md">{t('home.latestSolves')}</Heading>
        <Text fontSize="sm" color="text.muted">
          UPDATING IN {Math.ceil((60000 - (Date.now() % 60000)) / 1000)}s
        </Text>
      </HStack>

      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>Solver</Th>
            <Th display={{ base: 'none', md: 'table-cell' }}>Cube</Th>
            <Th display={{ base: 'none', md: 'table-cell' }}>Scramble</Th>
            <Th isNumeric>Time</Th>
            <Th display={{ base: 'none', md: 'table-cell' }}>When</Th>
          </Tr>
        </Thead>
        <Tbody>
          {solves.map((solve, index) => (
            <Tr 
              key={solve.id} 
              _hover={{ bg: hoverBg }}
            >
              <Td color="text.muted" fontSize={{ base: 'xs', md: 'sm' }}>{index + 1}</Td>
              <Td>
                <HStack spacing={2}>
                  <Avatar size="xs" src={solve.user.photoURL} name={solve.user.displayName} />
                  <Link 
                    as={RouterLink} 
                    to={`/u/${solve.user.username}`}
                    color={accentColor}
                    fontWeight="medium"
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    {solve.user.displayName}
                  </Link>
                </HStack>
              </Td>
              <Td display={{ base: 'none', md: 'table-cell' }}>
                {solve.cube && (
                  <Link
                    as={RouterLink}
                    to={`/cubes/${solve.cube.slug}`}
                    color="text.muted"
                    _hover={{ color: accentColor }}
                    fontSize={{ base: 'xs', md: 'sm' }}
                    onClick={(e) => {
                      e.stopPropagation();  // Prevent row click from triggering
                    }}
                  >
                    {solve.cube.brand} {solve.cube.model}
                  </Link>
                )}
              </Td>
              <Td display={{ base: 'none', md: 'table-cell' }} maxW="300px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                <Text color="text.muted" fontSize={{ base: 'xs', md: 'sm' }}>
                  {solve.scramble}
                </Text>
              </Td>
              <Td isNumeric>
                <HStack justify="flex-end" spacing={2}>
                  <Text fontFamily="mono" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                    {formatTime(solve.time)}
                  </Text>
                  {solve.penalty && (
                    <Badge colorScheme={solve.penalty === 'DNF' ? 'red' : 'yellow'} ml={1} fontSize={{ base: 'xs', md: 'sm' }}>
                      {solve.penalty}
                    </Badge>
                  )}
                </HStack>
              </Td>
              <Td display={{ base: 'none', md: 'table-cell' }} color="text.muted" fontSize={{ base: 'xs', md: 'sm' }}>
                {getTimeAgo(solve.timestamp)}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}; 